/**
 * Addresses that moved (#704). v2 left `/v2` for the root, and the classic dashboard went to escrow,
 * so an old link or bookmark is redirected (replacing its history entry) instead of meeting Not found:
 * a `/v2` address to the same screen without the prefix, a classic one to the v2 screen that holds
 * its job now, or to Today.
 */
import { createElement } from "react";
import { Navigate, useLocation, type RouteObject } from "react-router-dom";

/** Each classic section (its address's first segment) and where its job is now. */
const CLASSIC: Record<string, string> = {
  classic: "/",
  feedback: "/",
  members: "/people",
  membership: "/people",
  districts: "/people",
  licenses: "/people?view=licences",
  growth: "/people?view=all&report=growth",
  retention: "/people?view=all&report=renewals",
  renewals: "/people?view=all&report=renewals",
  composition: "/people?view=all&report=composition",
  scorecard: "/people?view=all&report=scorecard",
  "events-breakdown": "/events",
  "email-drafts": "/email",
  "audience-readiness": "/email?panel=readiness",
  "engagement-data": "/email?panel=engagement",
  // The sync went to Settings (#716): the reconciliation is in Data and sync.
  reconciliation: "/settings?open=sync",
  finance: "/money",
  revenue: "/money?panel=revenue",
  "data-sync": "/settings?open=sync",
  ops: "/settings?open=sync",
  "data-quality": "/settings?open=quality",
  exports: "/settings?open=exports",
  ...Object.fromEntries(["community", "forms", "fundraising", "governance", "notifications", "orgs", "partnerships", "store"].map((lab) => [lab, `/labs/${lab}`])),
};

/** The classic desk's review lists, now People views. */
const MEMBERSHIP: Record<string, string> = {
  held: "/people?view=held",
  "opted-out": "/people?view=opted-out",
  "not-in-ac": "/people?view=not-in-ac",
  "recently-added": "/people?view=recently-added-ac",
};

/** Where an old address lives now. `/login?next=` (the classic sign-in v2 used to send people to) goes on to `next` when it is on this site. */
export function movedTo(url: URL): string {
  const [, first = "", second = "", third = ""] = url.pathname.split("/");
  if (first === "v2") return `${url.pathname.slice(3) || "/"}${url.search}${url.hash}`;
  if (first === "login") {
    const next = new URL(url.searchParams.get("next") || "/", url.origin);
    return next.origin === url.origin ? `${next.pathname}${next.search}${next.hash}` : "/";
  }
  if (first === "members" && second) return `/people?person=${encodeURIComponent(second)}`;
  // A person's readiness evidence (/audience-readiness/person/:id) is their person sheet now.
  if (first === "audience-readiness" && second === "person" && third) return `/people?person=${encodeURIComponent(third)}`;
  const view = first === "membership" ? MEMBERSHIP[second] : undefined;
  return view ?? CLASSIC[first] ?? "/";
}

/**
 * Email's sheets that moved to Settings with the sync (#716): a run of the push log (`?run=`) opens in Settings' Sync
 * activity, and the ActiveCampaign reconciliation (`?panel=reconciliation`) is part of Data and sync. Null when the
 * address is Email's own.
 */
export function emailMovedTo(search: string): string | null {
  const params = new URLSearchParams(search);
  const run = params.get("run");
  if (run) return `/settings?${new URLSearchParams({ open: "activity", run })}`;
  if (params.get("panel") === "reconciliation") return "/settings?open=sync";
  return null;
}

export const movedRoutes: RouteObject[] = ["v2", "login", ...Object.keys(CLASSIC)].map((segment) => ({
  path: `${segment}/*`,
  Component: () => {
    const { pathname, search, hash } = useLocation();
    return createElement(Navigate, { to: movedTo(new URL(`${pathname}${search}${hash}`, window.location.origin)), replace: true });
  },
}));
